import { useCallback, useRef } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import {
  beginPendingGroupPersist,
  cancelPendingGroupPersists,
  claimPendingGroupPersist,
  isPendingGroupPersistCurrent,
} from '@/lib/groups/pending-group-persist'
import { persistForwardGroupApiFromShapes } from '@/lib/groups/persist-forward-group-api'
import { persistStructuralGroupHistoryIfApplicable } from '@/lib/groups/persist-structural-group-history'
import { logger } from '@/utils/logger'
import type { UndoAction } from '@/lib/history/types/undo-action'
import type { UndoAtomicAction } from '@/lib/history/types/undo-atomic-action'
import type { HistoryDirection } from '@/lib/history/types/history-direction'
import { applyHistoryAction } from '@/lib/history/undo-redo-engine'
import { reconcilePhotoPlacementOverridesWithShapes } from '@/lib/photos/sync-photo-placement-from-shapes'
import {
  reconcileClikArtPlacementsWithShapes,
  withShapesAdded,
} from '@/lib/clikart/sync-clikart-placement-from-shapes'
import { syncTemplatePlacementsWithShapes } from '@/lib/templates/sync-template-placement-from-shapes'
import { useWrapAffectedTextFrameResave } from '../use-wrap-affected-text-frame-resave'
import type {
  HomeDocumentLayout,
  HomeFoundation,
  HomePersistence,
  HomeZoomAndSpread,
} from '../../types/home-groups'

/**
 * Undo / redo: the shared history step (apply, persist, repair chains, resync the text panel),
 * batched history for pending shape deletes, and the Undo / Redo commands.
 */
export function useHomeHistory(
  deps: Pick<
    HomeFoundation,
    | 'shapeManagerRef'
    | 'centerCanvasRef'
    | 'chainProjectionBaselineRef'
    | 'textChains'
    | 'shapeTextBakeRef'
    | 'fontLookupRef'
  > &
    Pick<HomeZoomAndSpread, 'spreadIdRef'> &
    Pick<HomeDocumentLayout, 'spreadPlacementBounds' | 'spreadProjection'> &
    Pick<
      HomePersistence,
      | 'pushAction'
      | 'undo'
      | 'redo'
      | 'enqueueUpdate'
      | 'enqueueDelete'
      | 'enqueueDeletesAfterGroupRequests'
      | 'flushQueue'
      | 'enqueueGroup'
      | 'enqueueUngroup'
      | 'enqueueRestructure'
      | 'markEdited'
      | 'applyHistoryPersistencePlan'
    >
) {
  const {
    shapeManagerRef,
    centerCanvasRef,
    chainProjectionBaselineRef,
    spreadIdRef,
    spreadPlacementBounds,
    spreadProjection,
    pushAction,
    undo,
    redo,
    enqueueUpdate,
    enqueueDelete,
    enqueueDeletesAfterGroupRequests,
    flushQueue,
    enqueueGroup,
    enqueueUngroup,
    enqueueRestructure,
    markEdited,
    applyHistoryPersistencePlan,
  } = deps

  const resaveWrapAffectedTextFrames = useWrapAffectedTextFrameResave(deps)

  /**
   * Bridges `runHistoryStep` to the post-undo/redo chain repair (WMPCM-9199), which depends on
   * `redistributeChainStory` — and that only exists once `useHomeTextChains` runs, after this hook.
   * `useHomeTextChains` assigns it in a `useEffect` — the same bridge pattern
   * `shape-manager/hooks/use-gesture-commit.ts` uses for `handlePointerMoveRef`.
   */
  const repairChainsAfterHistoryStepRef = useRef<(touchedShapeIds: readonly string[]) => void>(
    () => {}
  )

  const runHistoryStep = useCallback(
    async (action: UndoAction, direction: HistoryDirection) => {
      // Captured before `applyHistoryAction` mutates the manager, for the chain rider — redoing a
      // whole-chain delete has to serialize a frame this step removes. See the
      // `linkedTextChainsUpsert` block in `applyHistoryPersistencePlan`.
      const shapesBeforeStep = shapeManagerRef.current?.getShapes() ?? []

      // A history step replays recorded paragraphs without re-flowing, so any pre-projection baseline
      // now describes content no frame holds. Unlike the seam memo — which self-invalidates on its
      // downstream key — this map has no such guard: a debounced commit landing after an undo would
      // compute both its history `before` and its persist set from the pre-undo state. Cleared
      // BEFORE the step, so nothing downstream of it can read a stale entry.
      chainProjectionBaselineRef.current.clear()

      const outcome = applyHistoryAction(action, direction, {
        shapeManager: shapeManagerRef.current,
      })
      const mgr = shapeManagerRef.current

      if (outcome.chromeToRestore && mgr) {
        mgr.restoreMultiSelectChrome(outcome.chromeToRestore)
        centerCanvasRef.current?.restoreUnifiedMultiSelectChrome(outcome.chromeToRestore)
      } else if (outcome.shouldResyncMultiSelectChrome) {
        queueMicrotask(() => {
          shapeManagerRef.current?.resyncMultiSelectChromeFromShapes()
          centerCanvasRef.current?.resyncUnifiedMultiSelectChromeFromSelection()
        })
      }

      // A group this step removes (undoing a placement, redoing a delete) may still be waiting to be
      // created on the server; its request would now make an empty group. Called off first, so the
      // plan's deletes don't wait for a request that is about to become a no-op, and before anything
      // below awaits.
      cancelPendingGroupPersists(outcome.persistence.shapeDeleteIds)

      applyHistoryPersistencePlan(outcome.persistence, shapesBeforeStep)

      // WMPCM-9199: `applyHistoryAction` replays each touched shape's recorded before/after
      // paragraphs verbatim and never re-derives the chain (see `redistributeChainStory`'s own
      // comment on this). Every FORWARD edit to a chain member always ends with a full
      // head-to-tail reflow; undo/redo had no equivalent, so a compound whose per-frame snapshots
      // weren't perfectly consistent with each other left the chain corrupted — content missing
      // from one member, or duplicated at the next reflow trigger — with no further undo able to
      // fix it, since the (already-wrong) snapshot IS what the next undo would replay too.
      //
      // Repair by re-deriving from current content, exactly like a normal edit would, for every
      // chain any shape this step touched belongs to. `skipHistoryPush: true` (applied inside the
      // ref target) is required here: this is bookkeeping that follows the user's undo/redo, not a
      // new action of their own, and must not itself become a second, unrelated entry on the stack.
      repairChainsAfterHistoryStepRef.current(outcome.persistence.shapeMarkEditedIds)

      // Undoing/redoing a wrap source (Text Wrap toggled, obstacle moved) changes how every unlinked
      // text frame under it lays out, but the plan above only re-saves the obstacle — so yb360,
      // which paints the frames' baked lines, kept the pre-undo wrap. Same re-save the forward
      // commit handlers do; see `enqueueWrapAffectedTextFrameSaves`.
      const touchedIds = new Set(outcome.persistence.shapeMarkEditedIds)
      const beforeById = new Map(shapesBeforeStep.map((s) => [s.id, s]))

      resaveWrapAffectedTextFrames(
        (mgr?.getShapes() ?? []).flatMap((after) => {
          const before = touchedIds.has(after.id) ? beforeById.get(after.id) : undefined

          return before ? [{ before, after }] : []
        }),
        outcome.persistence.historyTxnId,
        touchedIds
      )

      persistStructuralGroupHistoryIfApplicable({
        action,
        direction,
        plan: outcome.persistence,
        shapes: shapeManagerRef.current?.getShapes() ?? [],
        textBoxes: [],
        projection: spreadProjection,
        markEdited,
        enqueueGroup,
        enqueueUngroup,
        enqueueRestructure,
        enqueueDelete,
        enqueueUpdate,
        warn: (message, error) => logger.warn(message, error),
      })

      // Re-create / re-delete groups skipped by applyHistoryPersistencePlan for non-structural
      // transactions (e.g. delete: prefix, or a mixed paste: transaction — see
      // ShapeHistoryLifecycleMeta.isGroupCreation). group: and ungroup: are already handled above.
      const txnId = outcome.persistence.historyTxnId
      const isStructural = txnId?.startsWith('group:') || txnId?.startsWith('ungroup:')

      if (!isStructural) {
        // A kind-7 group can re-appear as a shapeUpsert with mode:'add' from either direction:
        // undoing a remove (restoring a previously deleted group) or redoing an add (e.g.
        // re-creating a pasted group). Both need the same forward-persist treatment.
        const recreatedGroupIds = new Set(
          outcome.persistence.shapeUpserts
            .filter((u) => u.mode === 'add' && u.shape?.kindId === 7)
            .map((u) => u.shape!.id)
        )

        if (recreatedGroupIds.size > 0) {
          const shapes = shapeManagerRef.current?.getShapes() ?? []
          const textBoxes: TextBoxData[] = []
          // Held across the wait below, so an undo of this very step calls the creation off.
          const pendingPersists = new Map<string, symbol>()

          for (const upsert of outcome.persistence.shapeUpserts) {
            // Inner groups are created by their outer group's request, so only that one is held.
            if (
              upsert.mode === 'add' &&
              upsert.shape?.kindId === 7 &&
              !recreatedGroupIds.has(upsert.shape.parentGroupId ?? '')
            ) {
              pendingPersists.set(upsert.shape.id, beginPendingGroupPersist(upsert.shape.id))
            }
          }

          // Children were cascade-deleted/removed and are re-added via ChangeQueue (300ms
          // debounce). POST /elements/group requires children to already exist in backend
          // storage, so flush the ChangeQueue first to ensure child adds complete before group
          // creation.
          await flushQueue()

          for (const upsert of outcome.persistence.shapeUpserts) {
            if (upsert.mode !== 'add' || !upsert.shape || upsert.shape.kindId !== 7) continue
            // Skip inner groups — outer group's restructure API call handles them transitively
            if (recreatedGroupIds.has(upsert.shape.parentGroupId ?? '')) continue

            const groupId = upsert.shape.id
            const pendingPersist = pendingPersists.get(groupId)

            if (!pendingPersist || !isPendingGroupPersistCurrent(groupId, pendingPersist)) continue

            const shouldSend = () => claimPendingGroupPersist(groupId, pendingPersist)

            let queued = false

            try {
              persistForwardGroupApiFromShapes({
                shapes,
                textBoxes,
                group: upsert.shape,
                historyTxnId: txnId,
                projection: spreadProjection,
                markEdited,
                enqueueGroup: (payload) => {
                  queued = true
                  enqueueGroup(payload, { shouldSend })
                },
                enqueueRestructure: (payload) => {
                  queued = true
                  enqueueRestructure(payload, { shouldSend })
                },
                warn: (message, error) => logger.warn(message, error),
              })
            } finally {
              // Nothing was queued (it declined, or threw): release the token instead of keeping it.
              if (!queued) claimPendingGroupPersist(groupId, pendingPersist)
            }
          }
        }

        if (direction === 'redo') {
          // shapeDeleteIds has only IDs for redo; use the action's remove entries for shape data
          const atomics: UndoAtomicAction[] = action.type === 'compound' ? action.actions : [action]
          const deletedGroupIds = new Set<string>()

          for (const a of atomics) {
            if (a.type === 'remove' && a.shape.kindId === 7) deletedGroupIds.add(a.shape.id)
          }

          for (const a of atomics) {
            if (a.type !== 'remove' || a.shape.kindId !== 7) continue
            // Only delete top-level groups — server cascade-deletes nested groups + children
            if (deletedGroupIds.has(a.shape.parentGroupId ?? '')) continue
            // Like the plan's own deletes, after any group request still on the wire.
            enqueueDeletesAfterGroupRequests([a.shape.id])
          }
        }
      }

      reconcilePhotoPlacementOverridesWithShapes(
        shapeManagerRef.current?.getShapes() ?? [],
        spreadPlacementBounds,
        spreadIdRef.current
      )

      // The ClikArt gallery's Placed badge (WMPCM-11057). `removeShape` publishes on the next
      // render, so `getShapes()` still lists whatever this step just removed — undoing an add (a
      // ClikArt drop) or redoing a delete — and the plan's own delete ids are discounted, or the
      // card would stay "Placed". The same lag runs the other way: `getShapes()` can miss a shape
      // this step just put back (WMPCM-13312: the second redo of a drop left its card unmarked), so
      // the plan's own adds are counted too.
      reconcileClikArtPlacementsWithShapes(
        withShapesAdded(
          shapeManagerRef.current?.getShapes() ?? [],
          outcome.persistence.shapeUpserts.flatMap((u) =>
            u.mode === 'add' && u.shape ? [u.shape] : []
          )
        ),
        spreadPlacementBounds,
        spreadIdRef.current,
        new Set(outcome.persistence.shapeDeleteIds)
      )

      // The Templates gallery's highlight: undoing a template placement removes all of its elements
      // (discounted for the same reason as above), so the highlight falls back to the previous one.
      syncTemplatePlacementsWithShapes(
        shapeManagerRef.current?.getShapes() ?? [],
        spreadIdRef.current,
        new Set(outcome.persistence.shapeDeleteIds)
      )
    },
    [
      spreadPlacementBounds,
      applyHistoryPersistencePlan,
      enqueueDelete,
      enqueueDeletesAfterGroupRequests,
      enqueueGroup,
      enqueueRestructure,
      enqueueUngroup,
      enqueueUpdate,
      flushQueue,
      markEdited,
      spreadProjection,
      centerCanvasRef,
      chainProjectionBaselineRef,
      resaveWrapAffectedTextFrames,
      shapeManagerRef,
      spreadIdRef,
    ]
  )

  const pendingShapeDeleteHistoryRef = useRef<ShapeData[]>([])
  const pendingShapeDeleteHistoryScheduledRef = useRef(false)

  const flushPendingShapeDeleteHistory = useCallback(() => {
    const pending = pendingShapeDeleteHistoryRef.current

    if (pending.length === 0) return

    pendingShapeDeleteHistoryRef.current = []
    pendingShapeDeleteHistoryScheduledRef.current = false

    if (pending.length === 1) {
      pushAction({ type: 'remove', shape: { ...pending[0] } })
      return
    }

    pushAction({
      type: 'batchRemove',
      shapes: pending.map((s) => ({ ...s })),
    })
  }, [pushAction])

  const queueShapeDeleteHistory = useCallback(
    (shape: ShapeData) => {
      pendingShapeDeleteHistoryRef.current.push(shape)
      if (pendingShapeDeleteHistoryScheduledRef.current) return

      pendingShapeDeleteHistoryScheduledRef.current = true
      queueMicrotask(() => {
        flushPendingShapeDeleteHistory()
      })
    },
    [flushPendingShapeDeleteHistory]
  )

  /**
   * Exits the active shape-text edit session only if the shape it was editing no longer exists
   * (e.g. undo/redo just removed it — undoing its own creation, or redoing its deletion). A
   * disappeared shape unmounts its `ShapeTextEditor` on its own, but the manager's own
   * `editingShapeTextId` ref/state would otherwise keep pointing at a dead id.
   */
  const exitShapeTextEditIfShapeGone = useCallback(() => {
    const editingId = shapeManagerRef.current?.getEditingShapeTextId()

    if (!editingId) return
    if (shapeManagerRef.current?.getShapes().some((s) => s.id === editingId)) return
    shapeManagerRef.current?.exitShapeTextEdit()
  }, [shapeManagerRef])

  /**
   * Runs after every undo/redo: drops a dead edit session, then nudges the Type panel's Font Size
   * field to re-read the (possibly still-selected) run's style. Undoing/redoing a style change
   * never moves the DOM text selection — the character range is untouched, only its style is — so
   * no native `selectionchange` fires, and the panel's `editSelectionTick` (the only thing that
   * makes it re-read `getEditingShapeTextSelectionStyle()` mid-session) never bumps. Without this,
   * the field kept showing the pre-undo/redo value until some UNRELATED re-render happened to
   * refresh it — in practice, the NEXT undo/redo click — so the very first Undo/Redo looked like it
   * "did nothing" even though the shape's data (and the canvas) had already changed. Same fix
   * `shape-text-editor/hooks/use-shape-text-keyboard.ts`'s own Up/Down-over-a-selection handler already applies to itself.
   */
  const syncTextPanelAfterHistoryStep = useCallback(() => {
    exitShapeTextEditIfShapeGone()

    // Release a currently-focused `[data-text-edit-keepalive]` control (a Type panel field's
    // input or slider, the menubar, ...) before the synthetic dispatch below. Those panel fields
    // deliberately skip a caret-driven resync while they hold focus, so typing isn't clobbered
    // mid-edit — but that guard has no way to tell "the user is still typing" apart from "Undo/Redo
    // just landed an authoritative model change while focus happened to be left on this control",
    // so without this, Undo/Redo with e.g. the Kerning input or slider focused updated the
    // underlying model correctly but never visibly updated that control until focus moved away.
    // Blurring first runs the control's own onBlur (which clears its focused guard) so the resync
    // this dispatch triggers actually takes effect immediately.
    const activeEl = document.activeElement as HTMLElement | null

    if (activeEl && activeEl !== document.body && activeEl.closest('[data-text-edit-keepalive]')) {
      activeEl.blur()
    }

    document.dispatchEvent(new Event('selectionchange'))
  }, [exitShapeTextEditIfShapeGone])

  const handleUndo = useCallback(() => {
    // Flushes any pending debounced text commit WITHOUT exiting the edit session (unlike
    // `finalizeActiveShapeTextEdit`, used here until WMPCM's undo-preserves-selection fix): undoing
    // a style change applied to a highlighted run inside the shape being edited must leave that
    // exact selection in place, or the Type panel re-reads the whole frame afterward and reports
    // "Mixed" even though the reverted range is uniform. `commitShapeTextNow`'s own baseline/current
    // diff (shared with `finalizeActiveShapeTextEdit`'s exit path) means this pushes the same
    // history entry either way, so the floor-checks below still compare against an accurate stack
    // size — only the "also exit editing" half is skipped.
    shapeManagerRef.current?.flushPendingShapeTextCommit()
    flushPendingShapeDeleteHistory()
    // A Text on a Path draft lives in `center-canvas/hooks/use-text-on-path-state.ts` React state and pushes NO history
    // entries until it is committed, so undo cannot see it — while Ctrl+Z still reaches here
    // from inside the bar, because its <textarea> holds focus all session and the form-field
    // guard above deliberately lets undo/redo through. Left alone, the history step reverts the
    // shape underneath a draft that still describes the pre-undo state, and the next commit
    // (Escape, or a click outside) writes that stale draft back over whatever was restored.
    //
    // Committing first makes the in-flight edit real so it can be undone like anything else, and
    // leaves no draft to go stale. Discarding instead would throw away what the user just typed —
    // worse than the bug. Not a `flushPending*` sibling despite the placement: shape text can flush
    // and keep editing because its baseline is resynced inside the engine, and there is no
    // equivalent re-seed for this draft (WMPCM-9554).
    centerCanvasRef.current?.exitTextOnPathMode()

    // The first Ctrl+Z that would reach back past node-edit mode's own entry point instead just
    // exits the mode (e.g. Edit Path on a shape converted from text via "Convert Text to Shape" —
    // exiting reveals the converted shape, not the original text; a further Ctrl+Z then undoes the
    // conversion itself). See `CenterCanvasHandle.isNodeEditUndoAtFloor`.
    if (centerCanvasRef.current?.isNodeEditUndoAtFloor?.()) {
      const exitedShapeId = centerCanvasRef.current.exitNodeEditMode()

      if (exitedShapeId) shapeManagerRef.current?.selectShapes([exitedShapeId])

      return
    }

    // While in crop mode, Ctrl+Z must stop once every edit made THIS crop session has been
    // undone — it must never reach back into whatever came before crop mode started (the shape's
    // original placement, an earlier edit, etc.) while still in crop mode. See
    // `CenterCanvasHandle.isCropUndoAtFloor`.
    if (centerCanvasRef.current?.isCropUndoAtFloor?.()) return

    // Background steps recorded on another spread are skipped (see `useHomePersistence`).
    const action = undo()

    if (action) {
      runHistoryStep(action, 'undo')
      centerCanvasRef.current?.syncNodeEditShapeFromManager()
      centerCanvasRef.current?.syncCropShapeFromManager()
      syncTextPanelAfterHistoryStep()
    }
  }, [
    flushPendingShapeDeleteHistory,
    undo,
    runHistoryStep,
    syncTextPanelAfterHistoryStep,
    centerCanvasRef,
    shapeManagerRef,
  ])

  const handleRedo = useCallback(() => {
    // See `handleUndo`'s comment: preserves the active edit session/selection across a redo of a
    // style change on the same shape instead of unconditionally exiting it.
    shapeManagerRef.current?.flushPendingShapeTextCommit()
    flushPendingShapeDeleteHistory()
    // See `handleUndo` — a live Text on a Path draft would otherwise clobber the redone state.
    centerCanvasRef.current?.exitTextOnPathMode()
    const action = redo()

    if (action) {
      runHistoryStep(action, 'redo')
      centerCanvasRef.current?.syncNodeEditShapeFromManager()
      centerCanvasRef.current?.syncCropShapeFromManager()
      syncTextPanelAfterHistoryStep()
    }
  }, [
    flushPendingShapeDeleteHistory,
    redo,
    runHistoryStep,
    syncTextPanelAfterHistoryStep,
    centerCanvasRef,
    shapeManagerRef,
  ])

  return {
    repairChainsAfterHistoryStepRef,
    runHistoryStep,
    pendingShapeDeleteHistoryRef,
    pendingShapeDeleteHistoryScheduledRef,
    flushPendingShapeDeleteHistory,
    queueShapeDeleteHistory,
    exitShapeTextEditIfShapeGone,
    syncTextPanelAfterHistoryStep,
    handleUndo,
    handleRedo,
  }
}
